import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import Navbar from "./components/Navbar";

import Home from "./pages/Home";
import Dashboard from "./pages/Dashboard";
import Converter from "./pages/Converter";
import About from "./pages/About";
import GoldBot from "./pages/GoldBot";
import SignInPage from "./pages/SignInPage";
import AcceptInvitationPage from "./pages/AcceptInvitationPage";
import MyTeam from "./pages/MyTeam";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminSubscribers from "./pages/admin/AdminSubscribers";
import AdminFreeTrials from "./pages/admin/AdminFreeTrials";
import RequireAuth from "./auth/RequireAuth";
import { Permission } from "./auth/permissions";

/**
 * Routes fall into three groups:
 *
 *  - public: the marketing pages, the market tools, and the Clerk sign-in
 *    experience;
 *  - authenticated: everything wrapped in `<RequireAuth>`;
 *  - admin: additionally gated on the `users:read` permission.
 *
 * The market Dashboard and the Converter are deliberately **public**. They read
 * public market data, hold nothing belonging to a user, and reach no platform
 * API. Wrapping them in `<RequireAuth>` — as an earlier revision did — made
 * them share GoldBot's fate: `<RequireAuth>` fails closed whenever `/api/me`
 * cannot be reached, so a backend misconfiguration blanked two pages that never
 * needed the backend at all.
 *
 * `<RequireAuth>` decides what to *render*. It is not a security boundary —
 * the backend re-checks the Clerk session and the MongoDB role on every
 * request, so hiding a route here never stands in for authorization there.
 */
function App() {
  return (
    <Router>
      <Navbar />

      <Routes>
        {/* public */}
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/converter" element={<Converter />} />

        {/* Clerk owns these screens; the platform never handles a password.

            OMJ VISION is invite-only, so sign-in is the only public
            authentication screen. `/sign-up/*` still exists because Clerk's
            invitation email lands there with a `__clerk_ticket`, but
            <AcceptInvitationPage> turns away anyone arriving without one. */}
        <Route path="/sign-in/*" element={<SignInPage />} />
        <Route path="/sign-up/*" element={<AcceptInvitationPage />} />

        {/* The pre-Clerk auth pages are gone. These redirects are all that
            remains of them, so old links and bookmarks still land somewhere. */}
        <Route path="/login" element={<Navigate to="/sign-in" replace />} />
        <Route path="/register" element={<Navigate to="/sign-in" replace />} />

        {/* authenticated */}
        <Route
          path="/goldbot/*"
          element={
            <RequireAuth permission={Permission.GOLDBOT_READ}>
              <GoldBot />
            </RequireAuth>
          }
        />

        <Route
          path="/my-team"
          element={
            <RequireAuth permission={Permission.MY_TEAM_READ}>
              <MyTeam />
            </RequireAuth>
          }
        />

        {/* admin */}
        <Route
          path="/admin/users"
          element={
            <RequireAuth permission={Permission.USERS_READ}>
              <AdminUsers />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/subscribers"
          element={
            <RequireAuth permission={Permission.SUBSCRIBERS_READ}>
              <AdminSubscribers />
            </RequireAuth>
          }
        />

        <Route
          path="/admin/free-trials"
          element={
            <RequireAuth permission={Permission.SUBSCRIBERS_READ}>
              <AdminFreeTrials />
            </RequireAuth>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
